// Pure, testable helpers for the Item detail page's Sales / Purchase history tabs.
//
// The ledger endpoint returns sales (estimate_items) and purchases (po_items) as
// positive movements, plus sales_returns / purchase_returns that REVERSE them. The UI
// folds returns into the same two tabs as signed (negative) rows, sorted newest-first,
// and shows footer totals. Keeping the merge + totals here (not inline in the page)
// makes them unit-testable and keeps the penny/quantity math in one place.

import type { EstimateStatus, POStatus } from '@billflow/shared';

// ─── Raw ledger row shapes (mirror GET /api/inventory/:id/ledger `data`) ─────────

export interface LedgerEstimateItem {
  estimate_id: number;
  estimate_number: string;
  date: string;
  status: EstimateStatus;
  qty: number;
  unit_price: number;
  line_total: number;
}

export interface LedgerSalesReturn {
  return_id: number;
  number: string;
  date: string;
  status: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

export interface LedgerPoItem {
  po_id: number;
  po_number: string;
  order_date: string;
  status: POStatus;
  qty: number;
  unit_price: number;
}

export interface LedgerPurchaseReturn {
  return_id: number;
  number: string;
  date: string;
  status: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

export interface LedgerReconciliation {
  opening_other: number;
  purchased: number;
  sold: number;
  sales_returns: number;
  purchase_returns: number;
  adjustments: number;
  current: number;
}

// ─── Merged (display) row shapes ─────────────────────────────────────────────────

export type SalesLedgerRow =
  | {
      kind: 'sale';
      id: number; // estimate_id → /estimates/:id
      number: string;
      date: string;
      status: EstimateStatus;
      qty: number; // positive
      unit_price: number;
      line_total: number; // positive
    }
  | {
      kind: 'return';
      id: number; // return_id → /returns/:id
      number: string;
      date: string;
      qty: number; // negative
      unit_price: number;
      line_total: number; // negative
    };

export type PurchaseLedgerRow =
  | {
      kind: 'purchase';
      id: number; // po_id → /purchase-orders/:id
      number: string;
      date: string;
      status: POStatus;
      qty: number; // positive
      unit_price: number;
    }
  | {
      kind: 'return';
      id: number; // return_id → /purchase-returns/:id
      number: string;
      date: string;
      qty: number; // negative
      unit_price: number;
      line_total: number; // negative
    };

// ─── Sorting ─────────────────────────────────────────────────────────────────────

// Newest-first. Dates are "YYYY-MM-DD" (or datetime) strings, so lexical compare is
// chronological. Array.sort is stable, so equal-date rows keep their input order.
function sortByDateDesc<T extends { date: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

// ─── Merge builders ──────────────────────────────────────────────────────────────

export function buildSalesRows(
  sales: LedgerEstimateItem[],
  returns: LedgerSalesReturn[],
): SalesLedgerRow[] {
  const rows: SalesLedgerRow[] = [
    ...sales.map(
      (s): SalesLedgerRow => ({
        kind: 'sale',
        id: s.estimate_id,
        number: s.estimate_number,
        date: s.date,
        status: s.status,
        qty: s.qty,
        unit_price: s.unit_price,
        line_total: s.line_total,
      }),
    ),
    ...returns.map(
      (r): SalesLedgerRow => ({
        kind: 'return',
        id: r.return_id,
        number: r.number,
        date: r.date,
        qty: -Math.abs(r.qty),
        unit_price: r.unit_price,
        line_total: -Math.abs(r.line_total),
      }),
    ),
  ];
  return sortByDateDesc(rows);
}

export function buildPurchaseRows(
  purchases: LedgerPoItem[],
  returns: LedgerPurchaseReturn[],
): PurchaseLedgerRow[] {
  const rows: PurchaseLedgerRow[] = [
    ...purchases.map(
      (p): PurchaseLedgerRow => ({
        kind: 'purchase',
        id: p.po_id,
        number: p.po_number,
        date: p.order_date,
        status: p.status,
        qty: p.qty,
        unit_price: p.unit_price,
      }),
    ),
    ...returns.map(
      (r): PurchaseLedgerRow => ({
        kind: 'return',
        id: r.return_id,
        number: r.number,
        date: r.date,
        qty: -Math.abs(r.qty),
        unit_price: r.unit_price,
        line_total: -Math.abs(r.line_total),
      }),
    ),
  ];
  return sortByDateDesc(rows);
}

// ─── Footer totals ───────────────────────────────────────────────────────────────

export interface SalesFooterTotals {
  netQty: number; // sold − returned
  salesTotal: number; // cents, positive
  returnsTotal: number; // cents, positive magnitude
}

export function computeSalesFooter(
  sales: LedgerEstimateItem[],
  returns: LedgerSalesReturn[],
): SalesFooterTotals {
  const salesQty = sales.reduce((sum, r) => sum + r.qty, 0);
  const returnsQty = returns.reduce((sum, r) => sum + Math.abs(r.qty), 0);
  const salesTotal = sales.reduce((sum, r) => sum + r.line_total, 0);
  const returnsTotal = returns.reduce((sum, r) => sum + Math.abs(r.line_total), 0);
  return { netQty: salesQty - returnsQty, salesTotal, returnsTotal };
}

export interface PurchaseFooterTotals {
  netQty: number; // purchased − returned
}

export function computePurchaseFooter(
  purchases: LedgerPoItem[],
  returns: LedgerPurchaseReturn[],
): PurchaseFooterTotals {
  const poQty = purchases.reduce((sum, r) => sum + r.qty, 0);
  const returnsQty = returns.reduce((sum, r) => sum + Math.abs(r.qty), 0);
  return { netQty: poQty - returnsQty };
}
